import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import Icon from '../../components/AppIcon';

const SocialPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('global'); // global, college, batch
    const [loading, setLoading] = useState(true);
    const [rankings, setRankings] = useState([]);
    const [friends, setFriends] = useState([]);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [activity, setActivity] = useState([]);
    const [userProfile, setUserProfile] = useState(null);

    useEffect(() => {
        fetchAllData();
    }, [activeTab]);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const { getAuthHeaders } = await import('../../utils/cookies');
            const headers = getAuthHeaders();

            // 1. Fetch User Profile (for context)
            const profileRes = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/profile`, { headers, credentials: 'include' });
            if (profileRes.ok) {
                const profile = await profileRes.json();
                setUserProfile(profile);
            }

            // 2. Fetch Rankings based on tab
            let rankingUrl = `${import.meta.env.VITE_API_BASE_URL}/api/rankings/global`;
            if (activeTab === 'college') rankingUrl = `${import.meta.env.VITE_API_BASE_URL}/api/rankings/college`;
            if (activeTab === 'batch') rankingUrl = `${import.meta.env.VITE_API_BASE_URL}/api/rankings/batch`;
            if (activeTab === 'friends') rankingUrl = `${import.meta.env.VITE_API_BASE_URL}/api/rankings/friends`;

            const rankingsRes = await fetch(rankingUrl, { headers, credentials: 'include' });
            if (rankingsRes.ok) {
                const data = await rankingsRes.json();
                setRankings(data || []);
            }

            // 3. Fetch Friends & Suggestions (only once initially ideally, but ok here)
            const friendsRes = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/friends`, { headers, credentials: 'include' });
            if (friendsRes.ok) {
                const fData = await friendsRes.json();
                setFriends(fData.friends || []);
                setPendingRequests(fData.pending || []);
            }

            const suggestionsRes = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/friends/suggestions`, { headers, credentials: 'include' });
            if (suggestionsRes.ok) {
                setSuggestions(await suggestionsRes.json() || []);
            }

            // 4. Fetch Friend Activity
            const activityRes = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/friends/activity`, { headers, credentials: 'include' });
            if (activityRes.ok) {
                setActivity(await activityRes.json() || []);
            }

        } catch (error) {
            console.error("Failed to load social data", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRespond = async (userId, status) => {
        try {
            const { getAuthHeaders } = await import('../../utils/cookies');
            const authHeaders = getAuthHeaders();
            const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/friends/respond`, {
                method: 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ user_id: userId, status })
            });

            if (res.ok) {
                // Remove from pending
                setPendingRequests(prev => prev.filter(req => req.user_id !== userId));
                // If accepted, maybe refresh friends list (optional, but good UX)
                if (status === 'accepted') {
                    // Optimistically add to friends or just refetch? Let's just refetch friends for simplicity
                    const friendsRes = await fetch(`${import.meta.env.VITE_API_BASE_URL}/friends`, { headers: authHeaders, credentials: 'include' });
                    if (friendsRes.ok) {
                        const fData = await friendsRes.json();
                        setFriends(fData.friends || []);
                    }
                }
            }
        } catch (error) {
            console.error("Failed to respond to friend request", error);
        }
    };

    const RankingRow = ({ rank, user }) => {
        const isMe = userProfile?.id === user.id;
        return (
            <div className={`grid grid-cols-12 gap-4 p-4 border-b border-slate-100 items-center hover:bg-slate-50 transition-colors ${isMe ? 'bg-emerald-50/50' : ''}`}>
                <div className="col-span-1 font-bold text-muted-foreground">#{rank}</div>
                <div className="col-span-6 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-primary flex items-center justify-center shrink-0">
                        {user.profile_picture_url ? (
                            <img src={user.profile_picture_url} className="w-full h-full object-cover" alt={user.username} />
                        ) : (
                            <span className="text-white text-xs font-bold">{user.username?.[0]?.toUpperCase()}</span>
                        )}
                    </div>
                    <div className="min-w-0">
                        <Link to={`/profile/${user.username}`} className="font-semibold text-foreground hover:text-primary hover:underline truncate block">
                            {user.full_name || user.username}
                        </Link>
                        <span className="text-xs text-muted-foreground truncate">@{user.username}</span>
                    </div>
                </div>
                <div className="col-span-3 text-sm text-foreground truncate">
                    {activeTab === 'batch' ? `Batch ${user.graduation_year}` : (user.college || '-')}
                </div>
                <div className="col-span-2 text-right font-bold text-primary">
                    {user.performance_score}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-white text-slate-900">
            <NavigationHeader />
            <main className="pt-16 pb-12 px-6">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">

                    {/* LEFT COLUMN: Activity & Friends (3 cols) */}
                    <div className="lg:col-span-3 space-y-6">

                        {/* Pending Requests */}
                        {pendingRequests.length > 0 && (
                            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden animate-in fade-in slide-in-from-top-2">
                                <div className="p-4 border-b border-slate-100 bg-emerald-50/30">
                                    <h3 className="font-bold flex items-center gap-2 text-primary">
                                        <Icon name="UserPlus" size={16} />
                                        Friend Requests
                                        <span className="bg-primary text-white text-[10px] px-1.5 py-0.5 rounded-full ml-auto">{pendingRequests.length}</span>
                                    </h3>
                                </div>
                                <div className="p-4 space-y-4">
                                    {pendingRequests.map((req) => (
                                        <div key={req.id} className="flex flex-col gap-3 pb-3 border-b border-border last:border-0 last:pb-0">
                                            <div className="flex items-center gap-2">
                                                <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center shrink-0 text-white text-xs">
                                                    {req.profile_picture_url ? <img src={req.profile_picture_url} className="w-full h-full rounded-full object-cover" /> : req.username[0].toUpperCase()}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold truncate">{req.full_name || req.username}</p>
                                                    <p className="text-xs text-muted-foreground truncate">@{req.username}</p>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <button
                                                    onClick={() => handleRespond(req.user_id, 'accepted')}
                                                    className="px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-md hover:bg-primary/90 transition-colors"
                                                >
                                                    Accept
                                                </button>
                                                <button
                                                    onClick={() => handleRespond(req.user_id, 'rejected')}
                                                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-bold rounded-lg hover:bg-slate-50 transition-colors"
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Friends Activity */}
                        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="font-bold flex items-center gap-2">
                                    <Icon name="Activity" size={16} className="text-primary" />
                                    Friend Activity
                                </h3>
                            </div>
                            <div className="p-4 space-y-4">
                                {activity.length > 0 ? activity.map((act, i) => (
                                    <div key={i} className="flex gap-3 text-sm">
                                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                                            {act.user_picture ? <img src={act.user_picture} className="w-full h-full rounded-full" /> : <Icon name="User" size={12} />}
                                        </div>
                                        <div>
                                            <p><span className="font-semibold">{act.full_name || act.username} <span className="text-muted-foreground font-normal text-xs">(@{act.username})</span></span> {act.action_description || 'completed an interview'}</p>
                                            <p className="text-xs text-muted-foreground">{new Date(act.timestamp).toLocaleDateString()}</p>
                                        </div>
                                    </div>
                                )) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">No recent activity</p>
                                )}
                            </div>
                        </div>

                        {/* Suggested Friends */}
                        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="font-bold flex items-center gap-2">
                                    <Icon name="UserPlus" size={16} className="text-primary" />
                                    Suggested for you
                                </h3>
                            </div>
                            <div className="p-4 space-y-4">
                                {suggestions.length > 0 ? suggestions.map((sug) => (
                                    <div key={sug.id} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <div className="w-8 h-8 rounded-full bg-gradient-primary flex items-center justify-center shrink-0 text-white text-xs">
                                                {sug.username[0].toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold truncate">{sug.full_name}</p>
                                                <p className="text-xs text-muted-foreground truncate">{sug.college}</p>
                                            </div>
                                        </div>
                                        <Link to={`/profile/${sug.username}`} className="text-primary hover:bg-primary/10 p-2 rounded-full transition-colors">
                                            <Icon name="ArrowRight" size={16} />
                                        </Link>
                                    </div>
                                )) : (
                                    <p className="text-sm text-muted-foreground text-center py-4">No suggestions right now</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* MIDDLE COLUMN: Rankings (6 cols) */}
                    <div className="lg:col-span-9">
                        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden min-h-[600px]">
                            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <h1 className="text-2xl font-bold flex items-center gap-2">
                                        <Icon name="Award" className="text-amber-500" />
                                        Leaderboard
                                    </h1>
                                    <p className="text-slate-500 text-sm">See how you stack up against the community</p>
                                </div>
                                <div className="flex p-1 bg-slate-100 rounded-xl">
                                    {[
                                        { id: 'global', label: 'Global' },
                                        { id: 'college', label: 'My College' },
                                        { id: 'batch', label: 'My Batch' },
                                        { id: 'friends', label: 'Friends' }
                                    ].map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setActiveTab(tab.id)}
                                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Rankings Header */}
                            <div className="grid grid-cols-12 gap-4 p-4 border-b border-slate-100 bg-slate-50 text-[10px] font-black text-slate-400tracking-widest leading-none">
                                <div className="col-span-1">Rank</div>
                                <div className="col-span-6">User</div>
                                <div className="col-span-3">{(activeTab === 'batch') ? 'Batch' : (activeTab === 'college' ? 'College' : (activeTab === 'friends' ? 'Relation' : 'College'))}</div>
                                <div className="col-span-2 text-right">Score</div>
                            </div>

                            {/* Rankings List */}
                            {loading ? (
                                <div className="flex items-center justify-center h-64">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                </div>
                            ) : rankings.length > 0 ? (
                                <div>
                                    {rankings.map((user, index) => (
                                        <RankingRow key={user.id} rank={index + 1} user={user} />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-64 text-center p-8">
                                    <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                                        <Icon name="Users" size={24} className="text-muted-foreground" />
                                    </div>
                                    <p className="text-lg font-normal text-muted-foreground">No rankings found</p>
                                    <p className="text-muted-foreground max-w-sm">
                                        {activeTab === 'college'
                                            ? "Add your college in settings to see how you compare with schoolmates."
                                            : "Start taking interviews to appear on the leaderboard!"}
                                    </p>
                                    {activeTab === 'college' && (
                                        <Button variant="outline" className="mt-4" onClick={() => navigate('/settings')}>
                                            Update Profile
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default SocialPage;
